import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, Badge, Flex, Spinner, Alert, AlertIcon,
  Table, Thead, Tbody, Tr, Th, Td,
  Stat, StatLabel, StatNumber, StatGroup, SimpleGrid,
  Button, HStack, Icon, Tooltip, Link,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDisc, FiRadio } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('meet_token')}`,
});

const STATUS_COLORS = {
  starting: 'yellow',
  active: 'green',
  stopping: 'orange',
  completed: 'gray',
  failed: 'red',
};

function formatDuration(startedAt, endedAt) {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = endedAt ? new Date(endedAt) : new Date();
  const diff = Math.max(0, Math.floor((end - start) / 1000));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
}

function secondsSince(isoString) {
  if (!isoString) return null;
  return Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
}

const StreamingStatus = () => {
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/recording-sessions/`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.results || []);
      list.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
      setSessions(list);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchSessions, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchSessions]);

  const activeSessions = (sessions || []).filter((s) => ['starting', 'active', 'stopping'].includes(s.status));
  const activeRecordings = activeSessions.filter((s) => s.type === 'recording');
  const activeStreams = activeSessions.filter((s) => s.type === 'stream');
  const failedRecent = (sessions || []).filter((s) => s.status === 'failed').length;

  // A session whose heartbeat has gone quiet for a while, despite Django
  // still believing it's active — the recorder Job may have crashed or been
  // killed without a clean SIGTERM/report cycle. 30s is 3x the recorder's
  // own 10s heartbeat interval (server.js), so this only flags a real stall.
  const isStale = (s) => ['starting', 'active', 'stopping'].includes(s.status)
    && s.last_heartbeat_at
    && secondsSince(s.last_heartbeat_at) > 30;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Streaming Status</Heading>
          <Text color="gray.500">Live and historical recording/streaming sessions</Text>
        </Box>
        <HStack>
          <Button size="sm" variant={autoRefresh ? 'solid' : 'outline'} colorScheme="teal"
            onClick={() => setAutoRefresh(!autoRefresh)}>
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button size="sm" leftIcon={<Icon as={FiRefreshCw} />} onClick={fetchSessions} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />{error}
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
          <StatGroup><Stat>
            <StatLabel><HStack spacing={1}><FiDisc /><Text>Active Recordings</Text></HStack></StatLabel>
            <StatNumber fontSize="lg">{activeRecordings.length}</StatNumber>
          </Stat></StatGroup>
        </Box>
        <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
          <StatGroup><Stat>
            <StatLabel><HStack spacing={1}><FiRadio /><Text>Active Streams</Text></HStack></StatLabel>
            <StatNumber fontSize="lg">{activeStreams.length}</StatNumber>
          </Stat></StatGroup>
        </Box>
        <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
          <StatGroup><Stat>
            <StatLabel>Failed Sessions</StatLabel>
            <StatNumber fontSize="lg" color={failedRecent > 0 ? 'red.500' : undefined}>{failedRecent}</StatNumber>
          </Stat></StatGroup>
        </Box>
      </SimpleGrid>

      {loading && !sessions ? <Spinner size="xl" /> : (
        <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Room</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Started</Th>
                <Th>Duration</Th>
                <Th>Bitrate</Th>
                <Th>Connection</Th>
                <Th>Started By</Th>
                <Th>Output / Destination</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(sessions || []).map((s) => (
                <Tr key={s.session_id} bg={isStale(s) ? 'orange.50' : undefined}>
                  <Td>{s.room_name || s.room}</Td>
                  <Td>
                    <HStack spacing={1}>
                      {s.type === 'recording' ? <FiDisc size={12} /> : <FiRadio size={12} />}
                      <Text fontSize="sm">{s.type === 'recording' ? 'Recording' : 'Stream'}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Badge colorScheme={STATUS_COLORS[s.status] || 'gray'}>{s.status}</Badge>
                      {isStale(s) && (
                        <Tooltip label={`No heartbeat for ${secondsSince(s.last_heartbeat_at)}s — the recorder may have crashed`}>
                          <Badge colorScheme="orange" variant="outline">stale</Badge>
                        </Tooltip>
                      )}
                    </HStack>
                  </Td>
                  <Td fontSize="xs">{s.started_at ? new Date(s.started_at).toLocaleString() : '-'}</Td>
                  <Td fontSize="xs">{formatDuration(s.started_at, s.ended_at)}</Td>
                  <Td fontSize="xs">{s.bitrate_kbps != null ? `${Math.round(s.bitrate_kbps)} kbps` : '-'}</Td>
                  <Td fontSize="xs">{s.connection_state || '-'}</Td>
                  <Td fontSize="xs">{s.started_by || '-'}</Td>
                  <Td fontSize="xs" maxW="280px" isTruncated>
                    {s.status === 'failed' ? (
                      <Tooltip label={s.error || 'Unknown error'}>
                        <Text color="red.500" isTruncated>{s.error || 'Failed'}</Text>
                      </Tooltip>
                    ) : s.output_url ? (
                      <Link href={s.output_url} isExternal color="teal.600">Recording file</Link>
                    ) : s.rtmp_url ? (
                      <Text isTruncated>{s.rtmp_url}</Text>
                    ) : '-'}
                  </Td>
                </Tr>
              ))}
              {(sessions || []).length === 0 && (
                <Tr><Td colSpan={9} textAlign="center" color="gray.500">No recording/streaming sessions yet</Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default StreamingStatus;
